<template>
    <div class="section">
        <div class="container">
            <h1 class="title is-4">
                <span class="icon mr-2"><i class="fas fa-hand-holding-heart"></i></span>
                Volunteer Interests
            </h1>

            <div v-if="interests.length === 0" class="notification is-light">
                No interest submissions yet.
            </div>

            <div v-else class="interest-list">
                <div
                    v-for="interest in interests"
                    :key="interest.id"
                    class="interest-card"
                >
                    <!-- Header: name + status -->
                    <div class="interest-card__header">
                        <div class="interest-card__volunteer">
                            <p class="has-text-weight-semibold">{{ interest.volunteerName }}</p>
                            <p class="is-size-7 has-text-grey">{{ interest.volunteerEmail }}</p>
                        </div>
                        <span class="tag" :class="statusClass(interest.status)">
                            {{ interest.status }}
                        </span>
                    </div>

                    <!-- Areas -->
                    <div class="tags mt-2">
                        <span
                            v-for="area in interest.areas"
                            :key="area"
                            class="tag is-primary is-light"
                        >{{ area }}</span>
                    </div>

                    <!-- Submitted date -->
                    <p class="is-size-7 has-text-grey mb-3">
                        <i class="fas fa-calendar-alt mr-1"></i>Submitted {{ formatDate(interest.createdAt) }}
                    </p>

                    <!-- Notes trail -->
                    <div class="notes-section">
                        <p class="notes-section__label">
                            <i class="fas fa-comments mr-1"></i>Notes
                        </p>

                        <div v-if="sortedNotes(interest).length === 0" class="notes-empty">
                            No notes yet.
                        </div>

                        <div v-else class="notes-trail">
                            <div
                                v-for="(note, idx) in sortedNotes(interest)"
                                :key="idx"
                                class="note-item"
                            >
                                <div class="note-item__meta">
                                    <span class="note-item__author">{{ note.authorName }}</span>
                                    <span class="note-item__date">{{ formatDate(note.createdAt) }}</span>
                                </div>
                                <p class="note-item__text">{{ note.text }}</p>
                            </div>
                        </div>

                        <!-- Add note + status actions -->
                        <div class="note-compose mt-3">
                            <textarea
                                class="textarea is-small"
                                rows="2"
                                placeholder="Add a note..."
                                v-model="newNotes[interest.id]"
                            ></textarea>
                            <div class="note-compose__actions mt-2">
                                <button
                                    class="button is-small is-primary"
                                    :disabled="!newNotes[interest.id] || !newNotes[interest.id].trim()"
                                    @click="submitNote(interest)"
                                >
                                    <span class="icon"><i class="fas fa-paper-plane"></i></span>
                                    <span>Post Note</span>
                                </button>
                                <button
                                    v-if="interest.status === 'NEW'"
                                    class="button is-small is-info is-light"
                                    @click="updateStatus(interest, 'IN_REVIEW')"
                                >
                                    <span class="icon"><i class="fas fa-eye"></i></span>
                                    <span>Mark In Review</span>
                                </button>
                                <button
                                    v-if="interest.status === 'IN_REVIEW'"
                                    class="button is-small is-success is-light"
                                    @click="updateStatus(interest, 'ACTIONED')"
                                >
                                    <span class="icon"><i class="fas fa-check"></i></span>
                                    <span>Mark Actioned</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            newNotes: {}
        };
    },
    created() {
        this.$store.dispatch('interests/subscribeInterests');
    },
    computed: {
        interests() {
            const order = { NEW: 0, IN_REVIEW: 1, ACTIONED: 2 };
            return [...this.$store.getters['interests/getInterests']]
                .sort((a, b) => (order[a.status] ?? 3) - (order[b.status] ?? 3));
        },
        currentUser() {
            return this.$store.getters['user/getCurrentUser'];
        }
    },
    methods: {
        statusClass(status) {
            if (status === 'NEW') return 'is-warning';
            if (status === 'IN_REVIEW') return 'is-info';
            if (status === 'ACTIONED') return 'is-success';
            return '';
        },
        formatDate(ts) {
            if (!ts) return '—';
            const d = ts.toDate ? ts.toDate() : new Date(ts);
            return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
        },
        sortedNotes(interest) {
            const trail = interest.notes_trail || [];
            return [...trail].sort((a, b) => {
                const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                return ta - tb;
            });
        },
        updateStatus(interest, status) {
            this.$store.dispatch('interests/updateInterest', { id: interest.id, status });
        },
        async submitNote(interest) {
            const text = (this.newNotes[interest.id] || '').trim();
            if (!text) return;
            await this.$store.dispatch('interests/addInterestNote', {
                id: interest.id,
                text,
                authorName: this.currentUser?.name || 'Admin',
                authorEmail: this.currentUser?.email || ''
            });
            this.newNotes[interest.id] = '';
        }
    }
}
</script>

<style scoped>
.interest-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.interest-card {
    background: #fff;
    border: 1.5px solid #e5e7eb;
    border-radius: 14px;
    padding: 1.1rem 1.25rem;
    box-shadow: 0 4px 14px -8px rgba(15, 23, 42, 0.12);
}

.interest-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.interest-card__volunteer p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Notes section */
.notes-section {
    border-top: 1px solid #f0f0f0;
    padding-top: 0.85rem;
}

.notes-section__label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 0.6rem;
}

.notes-empty {
    font-size: 0.85rem;
    color: #9ca3af;
    font-style: italic;
}

.notes-trail {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.note-item {
    background: #f8fafc;
    border-left: 3px solid #14b8a6;
    border-radius: 0 8px 8px 0;
    padding: 0.5rem 0.75rem;
}

.note-item__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.note-item__author {
    font-size: 0.78rem;
    font-weight: 700;
    color: #0f172a;
}

.note-item__date {
    font-size: 0.72rem;
    color: #9ca3af;
    white-space: nowrap;
}

.note-item__text {
    font-size: 0.875rem;
    color: #374151;
    white-space: pre-wrap;
    word-break: break-word;
}

.note-compose__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
}
</style>
